<template>
  <div class="teachers-page">
    <!-- Header -->
    <AppHeader />

    <!-- Hero Section -->
    <section class="teachers-hero">
      <div class="container">
        <div class="hero-content">
          <span class="teachers-eyebrow">Люди, которые вдохновляют</span>
          <h1>Наши преподаватели</h1>
          <p>Знакомим с языком, помогаем понять культуру
            и поддерживаем на каждом шаге.</p>
        </div>
      </div>
    </section>

    <!-- Teachers Section -->
    <UISection class="teachers-section">
      <template #default>
        <div class="teachers-grid" v-if="!loading && !error">
          <router-link v-for="teacher in teachers" :key="teacher.id" :to="`/teacher/${teacher.id}`" class="teacher-card">
            <div class="teacher-photo">
              <img v-if="teacher.photo" :src="teacher.photo" :alt="teacher.name" loading="lazy" decoding="async" />
              <i v-else class="fas fa-user" aria-hidden="true"></i>
            </div>
            <div class="teacher-info">
              <h2>{{ teacher.name }}</h2>
              <p class="teacher-title" v-if="teacher.label">{{ teacher.label }}</p>
              <span class="teacher-link">О преподавателе <span aria-hidden="true">↗</span></span>
            </div>
          </router-link>
        </div>
        <p v-if="!loading && !error && !teachers.length" class="empty-state">Информация о преподавателях скоро появится.</p>

        <!-- Loading state -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>Загрузка преподавателей...</p>
        </div>

        <!-- Error state -->
        <div v-if="error" class="error-state">
          <p>{{ error }}</p>
          <button @click="fetchTeachers" class="retry-button">Попробовать снова</button>
        </div>
      </template>
    </UISection>

    <!-- Testimonials Section -->
    <section v-if="reviews.length > 0" class="testimonial">
      <div class="container">
        <div class="testimonial-header">
          <h2 class="section-title" style="color: white;">Отзывы учеников</h2>
          <router-link to="/reviews" class="testimonial-link">Все отзывы</router-link>
        </div>
        <div class="testimonials-grid">
          <div class="testimonial-card" v-for="review in reviews" :key="review.id">
            <div class="testimonial-text">
              "{{ review.text }}"
            </div>
            <div class="testimonial-author">{{ review.student_name }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2>Готовы начать обучение с нашими преподавателями?</h2>
        <p>Запишитесь на бесплатный пробный урок и познакомьтесь с преподавателем</p>
        <UIButton to="/contacts#contact-methods">Записаться на урок</UIButton>
      </div>
    </section>

    <!-- Footer -->
    <UIFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import UIButton from '@/components/ui/UIButton.vue'
import UISection from '@/components/ui/UISection.vue'
import UIFooter from '@/components/ui/UIFooter.vue'
import axios from 'axios'

const api = axios.create({
  baseURL: '/api/v1'
})

export default {
  name: 'TeachersPage',
  components: {
    AppHeader,
    UIButton,
    UISection,
    UIFooter
  },
  data() {
    return {
      teachers: [],
      loading: true,
      error: null,
      reviews: []
    }
  },
  async mounted() {
    await this.fetchTeachers();
    await this.loadReviews();
  },
  methods: {
    async fetchTeachers() {
      try {
        this.loading = true;
        this.error = null;
        const response = await fetch('/api/v1/public/teachers');
        const data = await response.json();
        
        if (response.ok) {
          // Приводим имя к формату: только имя (без фамилии)
          this.teachers = (data || []).map(t => {
            const firstName = t.first_name?.trim();
            return {
              ...t,
              name: firstName || t.name || ''
            };
          });
        } else {
          this.error = 'Ошибка загрузки учителей';
        }
      } catch (error) {
        this.error = 'Ошибка соединения с сервером';
        console.error('Error fetching teachers:', error);
      } finally {
        this.loading = false;
      }
    },
    async loadReviews() {
      try {
        const { data } = await api.get('/public/reviews/random', {
          params: { count: 3 }
        })
        this.reviews = data || []
      } catch (err) {
        console.error('Не удалось загрузить отзывы', err)
      }
    }
  }
}
</script>

<style scoped>
.teachers-page { background: #f7f5f1; color: #302e2b; }
.teachers-hero { padding: 140px 0 48px; text-align: left; }
.teachers-eyebrow { color: #856d54; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; }
.teachers-hero h1 { margin: 14px 0 20px; font-size: clamp(2.25rem, 4.5vw, 3.75rem); font-weight: 500; line-height: 1.15; }
.teachers-hero p { max-width: 540px; margin: 0; font-size: 1.1rem; color: #746d65; line-height: 1.75; }
.teachers-section { padding: 0 0 80px; }
.teachers-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 28px; }
.teacher-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px;
  background: #fffefa;
  border: 1px solid #e7e1d8;
  border-radius: 16px;
  color: inherit;
  text-decoration: none;
  overflow: hidden;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.teacher-card:hover { border-color: #baa58f; box-shadow: 0 16px 36px -24px rgba(55, 43, 30, 0.25); }
.teacher-card:focus-visible { outline: 3px solid #833c3f; outline-offset: 4px; }
.teacher-photo { width: 100%; aspect-ratio: 4 / 5; border-radius: 8px; overflow: hidden; background: #eee9e1; display: flex; align-items: center; justify-content: center; color: #9c8d7e; font-size: 3rem; }
.teacher-photo img { width: 100%; height: 100%; object-fit: cover; object-position: center 30%; }
.teacher-info { display: flex; flex: 1; flex-direction: column; align-items: flex-start; padding: 22px 12px 10px; min-width: 0; }
.teacher-info h2 { margin: 0 0 8px; font-size: 1.5rem; font-weight: 500; line-height: 1.3; overflow-wrap: anywhere; }
.teacher-title { color: #746d65; font-size: 0.9rem; line-height: 1.6; margin: 0 0 24px; overflow-wrap: anywhere; }
.teacher-link { display: flex; align-items: center; justify-content: space-between; width: 100%; gap: 12px; padding-top: 16px; margin-top: auto; border-top: 1px solid #eae5dd; color: #833c3f; font-size: 0.85rem; font-weight: 600; }
.teacher-link span { font-size: 1.2rem; }
.empty-state { padding: 32px 0; color: #746d65; }
.cta-section { background: #302e2b; }
.cta-section h2 { font-size: clamp(1.65rem, 3vw, 2.5rem); line-height: 1.3; overflow-wrap: anywhere; }
.cta-section .btn { background: #833c3f; border-color: #833c3f; box-shadow: none; }
@media (max-width: 1000px) {
  .teachers-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
}
@media (max-width: 600px) {
  .teachers-hero { padding: 112px 0 32px; }
  .teachers-hero p { font-size: 1rem; }
  .teachers-grid { grid-template-columns: minmax(0, 1fr); gap: 24px; }
  .teacher-card { width: 100%; max-width: 420px; justify-self: center; }
  .teacher-info { padding: 20px 12px 12px; }
  .teachers-section { padding-bottom: 48px; }
  .testimonial-header { align-items: flex-start; flex-direction: column; gap: 12px; }
}
@media (prefers-reduced-motion: reduce) {
  .teacher-card { transition: none; }
}

.loading-state, .error-state {
  text-align: center;
  padding: 4rem 0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #e74c3c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.retry-button {
  display: inline-block;
  background: #e74c3c;
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 6px;
  text-decoration: none;
  margin-top: 1rem;
  transition: background 0.3s ease;
  border: none;
  cursor: pointer;
}

.retry-button:hover {
  background: #c0392b;
}

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2rem;
}

.testimonial {
  padding: 80px 0;
  background: #111827;
  color: #f1f5f9;
}

.testimonial-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
}

.testimonial-link {
  color: rgba(255,255,255,0.75);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

.testimonial-link:hover {
  color: #ffffff;
}

.testimonial-card {
  background: rgba(15, 23, 42, 0.45);
  padding: 2.5rem;
  border-radius: 18px;
  border: 1px solid rgba(100, 116, 139, 0.35);
  box-shadow: 0 25px 50px -35px rgba(15, 23, 42, 0.8);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  color: #e2e8f0;
}

.testimonial-text {
  font-size: 1.05rem;
  line-height: 1.8;
  color: rgba(248,250,252,0.9);
}

.testimonial-author {
  font-weight: 700;
  letter-spacing: 0.08em;
  color: rgba(226,232,240,0.85);
  text-transform: uppercase;
  font-size: 0.75rem;
}

</style>
